/**
* Localized NAATI comparison cluster renderers — /{locale}/naati/compare and
* /{locale}/naati/compare/{slug}. Content comes from hand-written per-locale
* copy in data/naati/compare-locale.ts (no machine translation).
*/
import { Link } from "@tanstack/react-router";
import type { NaatiLocale } from "@/data/naati/locale-copy";
import { NAATI_COMPARE_LOCALE, type NaatiCompareLocaleItem } from "@/data/naati/compare-locale";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedClusters } from "@/components/RelatedClusters";
import { ContentFreshness } from "@/components/ContentFreshness";

export function NaatiLocaleCompareHub({ locale }: { locale: NaatiLocale }) {
  const copy = NAATI_COMPARE_LOCALE[locale];
  if (!copy) return null;
  const L = copy.labels;
  return (
    <>
      <Breadcrumbs
        items={[
          { label: L.breadcrumbHome, to: "/" },
          { label: L.breadcrumbNaati },
          { label: L.breadcrumbCompare },
        ]}
      />
      <section className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl font-bold mb-4">{copy.hubH1}</h1>
        <p className="lead text-muted-foreground mb-8" data-speakable>
          {copy.hubLead}
        </p>
        <ul className="space-y-4">
          {copy.items.map((it) => (
            <li key={it.slug} className="border rounded-xl p-5">
              <h2 className="font-semibold mb-1">
                <Link
                  to="/$locale/naati/compare/$slug"
                  params={{ locale, slug: it.slug }}
                  className="text-blue-700 hover:underline"
                >
                  {it.h1}
                </Link>
              </h2>
              <p className="text-sm text-muted-foreground">{it.description}</p>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground mt-8">{L.disclaimer}</p>
      </section>
    </>
  );
}

export function NaatiLocaleComparePage({
  locale,
  item,
}: {
  locale: NaatiLocale;
  item: NaatiCompareLocaleItem;
}) {
  const copy = NAATI_COMPARE_LOCALE[locale];
  if (!copy) return null;
  const L = copy.labels;
  const others = copy.items.filter((c) => c.slug !== item.slug).slice(0, 5);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: L.breadcrumbHome, to: "/" },
          { label: L.breadcrumbNaati },
          { label: L.breadcrumbCompare },
          { label: item.h1 },
        ]}
      />
      <section className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-3xl font-bold mb-5">{item.h1}</h1>
        <div className="answer-summary text-lg mb-8 p-4 bg-muted rounded-xl" data-speakable>
          {item.answer}
        </div>

        <h2 className="text-xl font-semibold mb-3">{L.table}</h2>
        <div className="overflow-x-auto mb-8">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-3 font-semibold">&nbsp;</th>
                <th className="text-left py-2 pr-3 font-semibold">{item.aLabel}</th>
                <th className="text-left py-2 font-semibold">{item.bLabel}</th>
              </tr>
            </thead>
            <tbody>
              {item.rows.map((r) => (
                <tr key={r.aspect} className="border-b align-top">
                  <td className="py-2 pr-3 font-medium">{r.aspect}</td>
                  <td className="py-2 pr-3">{r.a}</td>
                  <td className="py-2">{r.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 mb-8">
          <div className="border rounded-xl p-5">
            <h2 className="font-semibold mb-2">
              {item.aLabel} — {L.chooseWhen}
            </h2>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {item.chooseA.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
          <div className="border rounded-xl p-5">
            <h2 className="font-semibold mb-2">
              {item.bLabel} — {L.chooseWhen}
            </h2>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {item.chooseB.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-3">{L.faq}</h2>
        <div className="space-y-5 mb-8">
          {item.faqs.map((f) => (
            <article key={f.q} className="border-b pb-4">
              <h3 className="font-semibold mb-1">{f.q}</h3>
              <p className="faq-answer text-sm leading-relaxed" data-speakable>
                {f.a}
              </p>
            </article>
          ))}
        </div>

        <div className="p-6 bg-muted rounded-xl mb-8">
          <p className="font-semibold">{L.cta}</p>
        </div>
        <p className="text-xs text-muted-foreground mb-8">{L.disclaimer}</p>

        <h2 className="text-xl font-semibold mb-3">{L.other}</h2>
        <ul className="space-y-2 text-sm">
          {others.map((o) => (
            <li key={o.slug}>
              <Link
                to="/$locale/naati/compare/$slug"
                params={{ locale, slug: o.slug }}
                className="text-blue-700 hover:underline"
              >
                {o.h1}
              </Link>
            </li>
          ))}
        </ul>
      </section>
      <RelatedClusters locale={locale as never} exclude={["naati"]} />
      <ContentFreshness path={`/${locale}/naati/compare/${item.slug}`} locale={locale as never} />
    </>
  );
}
